// Interaction with objects in the room (contract section 7).
//
// Only the interactive objects are raycast (meshes carrying extras.interactive, or the loader's fallback):
// a small separate list, tested once per animation frame at most, on pointer moves (not while dragging) and
// after the camera moved under a resting cursor.
//  - hover: a brightness lift over the object (an overlay pass of the same meshes: dst * (1 + LIFT), drawn only
//    while hovered), cursor: pointer, and a small label with the object's name next to the cursor;
//  - click: select -> outline (one inverted hull of the object's merged geometry, offset along smooth normals
//    by a constant number of pixels in clip space; no post-processing);
//  - click the selected object again: open (the app opens the panel). Click on nothing: the app closes the
//    panel if it is open, else the selection is cleared.
// Surfaces (the hang slots, src/art/slots.ts) are picked by a SurfacePicker with plain math, in the same raycast;
// the nearer hit wins. A surface gets no hover lift and no label (the hang slots' label is empty), only the
// cursor; a click selects it and opens it at once; its selection outline is drawn by the picker (showSelected).
// Shaped targets (2026-10-29, the floor portals): picked through their own meshes (a pick box), but the lift and the
// outline go round other meshes (the ladder), and the outline shows on hover as well as on selection.

import {
  BackSide,
  BufferAttribute,
  BufferGeometry,
  Color,
  CustomBlending,
  DstColorFactor,
  Group,
  LessEqualDepth,
  Mesh,
  MeshBasicMaterial,
  OneFactor,
  AddEquation,
  Raycaster,
  ShaderMaterial,
  SRGBColorSpace,
  Vector2,
  Vector3,
  type PerspectiveCamera,
  type Scene,
} from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** Brightness lift while hovered (fraction). */
const LIFT = 0.18;
/** Outline width in CSS pixels. */
const OUTLINE_PX = 2.5;

const NAMES: Record<string, string> = { room_controls: 'Listening Room A', skull: 'Skull', globe: 'Globe' };
export const interactiveName = (id: string) => NAMES[id] ?? id.replace(/_/g, ' ');
/** Hover label for an interactive id added at run time (portals: "To <room title>"). */
export const setInteractiveName = (id: string, name: string) => { NAMES[id] = name; };

interface Target {
  id: string;
  meshes: Mesh[];
  highlight: Group;
  outline: Mesh;
  /** lift and outline drawn round shape meshes instead of the pick meshes; outlined on hover too */
  shaped: boolean;
}

/** Things to pick that are not meshes (hang slots on the walls). Ids must not clash with interactive ids. */
export interface SurfacePicker {
  pick(ray: Raycaster['ray']): { id: string; distance: number } | null;
  owns(id: string): boolean;
  /** Hover label; '' = none (cursor only). */
  label(id: string): string;
  /** The selection changed (any id, or null): draw or hide the surface's own outline. */
  showSelected(id: string | null): void;
}

export interface InteractionEvents {
  /** The selected object was clicked again. */
  open(id: string): void;
  /** A click hit nothing. Return true if the app used it (e.g. closed the panel): the selection stays. */
  empty(): boolean;
  /** Something changed on screen. */
  invalidate(): void;
}

export class Interaction {
  private targets = new Map<string, Target>();
  private meshes: Mesh[] = [];
  private raycaster = new Raycaster();
  private ndc = new Vector2();
  private pointer = { x: 0, y: 0, inside: false };
  private pending = false;
  private raf = 0;
  private dragging = false;
  hovered: string | null = null;
  selected: string | null = null;
  private root = new Group();
  private label: HTMLDivElement;
  private highlightMat: MeshBasicMaterial;
  private outlineMat: ShaderMaterial;
  private surfaces: SurfacePicker | null = null;
  /** Raycasts done (for tests). */
  readonly stats = { raycasts: 0 };

  constructor(scene: Scene, private camera: PerspectiveCamera, private canvas: HTMLCanvasElement, private events: InteractionEvents) {
    this.root.name = 'interaction';
    this.root.matrixAutoUpdate = false;
    scene.add(this.root);
    this.label = document.createElement('div');
    this.label.id = 'target-label';
    this.label.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.label);
    // out = src * dst + dst = dst * (1 + LIFT); src is written in output (sRGB) space, so give it in sRGB
    this.highlightMat = new MeshBasicMaterial({
      color: new Color().setRGB(LIFT, LIFT, LIFT, SRGBColorSpace),
      transparent: true,
      depthWrite: false,
      depthFunc: LessEqualDepth,
      blending: CustomBlending,
      blendEquation: AddEquation,
      blendSrc: DstColorFactor,
      blendDst: OneFactor,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
      toneMapped: false,
    });
    this.outlineMat = new ShaderMaterial({
      uniforms: { uColor: { value: new Color(0xfff4dc) }, uPx: { value: OUTLINE_PX }, uRes: { value: new Vector2(1, 1) } },
      vertexShader: /* glsl */ `
        uniform float uPx;
        uniform vec2 uRes;
        void main() {
          vec4 clip = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          vec3 vn = normalize( normalMatrix * normal );
          vec2 d = ( projectionMatrix * vec4( vn, 0.0 ) ).xy;
          float l = length( d );
          if ( l > 1e-6 ) d /= l;
          clip.xy += d * uPx * 2.0 / uRes * clip.w;
          gl_Position = clip;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        void main() {
          gl_FragColor = vec4( uColor, 1.0 );
          #include <colorspace_fragment>
        }`,
      side: BackSide,
      depthWrite: false,
      toneMapped: false,
    });
    addEventListener('blur', () => this.pointerLeave());
  }

  /**
   * Use the interactive meshes of a (new) loaded scene. Keeps the selection by id. `shapes`: for some ids, the
   * meshes the lift and outline go round instead of the picked ones (world-space geometry, not in the scene).
   */
  setScene(interactive: Map<string, Mesh[]>, shapes: Map<string, Mesh[]> = new Map()): void {
    for (const t of this.targets.values()) {
      t.highlight.removeFromParent();
      t.outline.removeFromParent();
      t.outline.geometry.dispose();
    }
    this.targets.clear();
    this.meshes = [];
    for (const [id, meshes] of interactive) {
      const highlight = new Group();
      highlight.matrixAutoUpdate = false;
      for (const m of meshes) {
        m.geometry.computeBoundingSphere();
        this.meshes.push(m);
      }
      const shape = shapes.get(id);
      const drawn = shape?.length ? shape : meshes;
      for (const m of drawn) {
        const h = new Mesh(m.geometry, this.highlightMat);
        h.matrixAutoUpdate = false;
        h.matrix.copy(m.matrixWorld);
        h.matrixWorld.copy(m.matrixWorld);
        h.renderOrder = 20;
        highlight.add(h);
      }
      highlight.visible = false;
      const outline = new Mesh(hullGeometry(drawn), this.outlineMat);
      outline.matrixAutoUpdate = false;
      outline.renderOrder = 21;
      outline.visible = false;
      outline.frustumCulled = false;
      this.root.add(highlight, outline);
      this.targets.set(id, { id, meshes, highlight, outline, shaped: drawn !== meshes });
    }
    if (this.selected && !this.targets.has(this.selected) && !this.surfaces?.owns(this.selected)) this.selected = null;
    // a hovered id that is gone: hide its label and cursor too (setHover), not only the lift
    if (this.hovered && !this.targets.has(this.hovered) && !this.surfaces?.owns(this.hovered)) this.setHover(null);
    this.show();
  }

  /**
   * Forget the hover (a room switch): no lift, outline, label or pointer cursor until the pointer moves again (a
   * resting cursor is not raycast against the new room on camera moves).
   */
  clearHover(): void {
    this.pointer.inside = false;
    this.pending = false;
    this.setHover(null);
  }

  setSurfaces(s: SurfacePicker | null): void {
    this.surfaces = s;
    this.schedule();
  }

  /** The selection outline's material (constant pixel width; shared by other outlines). */
  get outlineMaterial(): ShaderMaterial {
    return this.outlineMat;
  }

  /** The drawing buffer size uniform, for other screen-space outlines. */
  get outlineResolution(): { value: Vector2 } {
    return this.outlineMat.uniforms.uRes as { value: Vector2 };
  }

  get count(): number {
    return this.targets.size;
  }

  /** Drawing buffer size changed (outline width is in CSS pixels). */
  resize(w: number, h: number): void {
    this.outlineMat.uniforms.uRes!.value.set(w, h);
  }

  pointerMove(x: number, y: number): void {
    this.pointer.x = x;
    this.pointer.y = y;
    this.pointer.inside = true;
    this.schedule();
  }

  pointerLeave(): void {
    this.pointer.inside = false;
    this.setHover(null);
  }

  setDragging(d: boolean): void {
    this.dragging = d;
    if (d) this.setHover(null);
    else this.schedule();
  }

  /** The camera moved: the object under a resting cursor may have changed (call from the frame loop). */
  cameraMoved(): void {
    if (this.pointer.inside && !this.dragging) this.pending = true;
  }

  /** Run a pending raycast (call once per frame from the render loop). */
  frame(): void {
    if (!this.pending) return;
    this.pending = false;
    if (!this.pointer.inside || this.dragging) return;
    this.setHover(this.pick(this.pointer.x, this.pointer.y));
  }

  private schedule(): void {
    this.pending = true;
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.frame();
    });
  }

  /** Interactive id (or surface id) under client point (x, y), or null. */
  pick(x: number, y: number): string | null {
    if (!this.meshes.length && !this.surfaces) return null;
    const r = this.canvas.getBoundingClientRect();
    this.ndc.set(((x - r.left) / r.width) * 2 - 1, -((y - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    this.stats.raycasts++;
    const hit = this.meshes.length ? this.raycaster.intersectObjects(this.meshes, false)[0] : undefined;
    const surf = this.surfaces?.pick(this.raycaster.ray) ?? null;
    if (surf && (!hit || surf.distance < hit.distance)) return surf.id;
    return hit ? ((hit.object.userData.interactive as string) ?? null) : null;
  }

  /** The label for an id (surface or object). */
  labelOf(id: string): string {
    return this.surfaces?.owns(id) ? this.surfaces.label(id) : interactiveName(id);
  }

  /** Refresh the hover label (a surface's label changed, e.g. a picture was hung). */
  refreshLabel(): void {
    if (this.hovered) this.setLabel(this.labelOf(this.hovered));
  }

  /** Show `text` next to the cursor; an empty text hides the label (hang slots: cursor only). */
  private setLabel(text: string): void {
    this.label.textContent = text;
    this.label.classList.toggle('on', !!text);
  }

  click(x: number, y: number, touch: boolean): void {
    const id = this.pick(x, y);
    if (!touch) {
      this.pointerMove(x, y);
    }
    if (id) {
      if (this.surfaces?.owns(id)) {
        // a hang slot: select and open at once
        this.select(id);
        this.events.open(id);
      } else if (this.selected === id) this.events.open(id);
      else this.select(id);
      return;
    }
    if (!this.events.empty()) this.select(null);
  }

  select(id: string | null): void {
    if (this.selected === id) return;
    this.selected = id;
    this.surfaces?.showSelected(id);
    this.show();
  }

  private setHover(id: string | null): void {
    if (this.hovered === id) {
      if (id) this.moveLabel();
      return;
    }
    this.hovered = id;
    this.canvas.style.cursor = id ? 'pointer' : '';
    if (id) {
      this.moveLabel();
      this.setLabel(this.labelOf(id));
    } else {
      this.setLabel('');
    }
    this.show();
  }

  private moveLabel(): void {
    this.label.style.transform = `translate3d(${Math.round(this.pointer.x + 16)}px, ${Math.round(this.pointer.y + 18)}px, 0)`;
  }

  private show(): void {
    for (const t of this.targets.values()) {
      t.highlight.visible = t.id === this.hovered;
      t.outline.visible = t.id === this.selected || (t.shaped && t.id === this.hovered);
    }
    this.events.invalidate();
  }
}

/** One geometry for the outline: the object's meshes in world space, welded, with smooth normals. */
export function hullGeometry(meshes: Mesh[]): BufferGeometry {
  const parts: BufferGeometry[] = [];
  for (const m of meshes) {
    const src = m.geometry;
    const pos = src.attributes.position!;
    const arr = new Float32Array(pos.count * 3);
    const v = new Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld);
      arr[i * 3] = v.x; arr[i * 3 + 1] = v.y; arr[i * 3 + 2] = v.z;
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(arr, 3));
    if (src.index) g.setIndex(new BufferAttribute(Uint32Array.from(src.index.array as ArrayLike<number>), 1));
    // mergeGeometries needs all indexed or none; mergeVertices below indexes again
    parts.push(g.index ? g.toNonIndexed() : g);
  }
  const merged = parts.length === 1 ? parts[0]! : mergeGeometries(parts, false) ?? parts[0]!;
  const welded = mergeVertices(merged, 1e-4);
  welded.computeVertexNormals();
  return welded;
}
